// Phase 14e-a — /admin/rates/cards.
//
// Consumes the shared <SupersedableResourcePanel>. ~50 LOC of config
// passes columns + form fields + endpoint callbacks; the panel handles
// the section partitioning, modals, lead-time gate, localStorage state.

import { createFileRoute } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { z } from 'zod';
import { unwrap } from '@truecare/api-client';
import { api } from '../../api';
import { SupersedableResourcePanel } from '../../components/SupersedableResourcePanel';

export const Route = createFileRoute('/rates/cards')({
  component: RateCardsPage,
});

const SETTING_OPTIONS = [
  { value: 'LTC', label: 'LTC' },
  { value: 'HOMECARE', label: 'Homecare' },
] as const;

const HST_OPTIONS = [
  { value: 'TAXABLE', label: 'Taxable' },
  { value: 'EXEMPT', label: 'Exempt' },
  { value: 'ZERO_RATED', label: 'Zero-rated' },
] as const;

// UI form schemas — flat z.object (do NOT use the API intersection
// schemas; zodResolver doesn't handle .and() refinements predictably).
// Exported for the audit-#12 guard test (cards.schema.spec.ts) — pins the
// homecare-must-be-taxable compliance rule at the FE layer.
export const CREATE_SCHEMA = z
  .object({
    roleId: z.string().uuid('Pick a role'),
    setting: z.enum(['LTC', 'HOMECARE']),
    defaultPayRate: z.string().regex(/^\d+(\.\d{1,4})?$/, 'Enter a positive decimal'),
    defaultBillRate: z.string().regex(/^\d+(\.\d{1,4})?$/, 'Enter a positive decimal'),
    hstTreatment: z.enum(['TAXABLE', 'EXEMPT', 'ZERO_RATED']),
    effectiveFrom: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
    reason: z.string().min(10, 'Min 10 characters').max(500),
    overrideLeadTime: z.boolean().optional(),
    overrideReason: z.string().optional(),
  })
  // Homecare is always HST-taxable (operator decision 2026-07-28); only facility
  // (LTC) rate cards may be exempt. superRefine on a flat z.object works with
  // zodResolver (the .and()-refinement caveat above is about intersections, not
  // this). Backend enforces the same rule — this is the inline-error mirror.
  .superRefine((val, ctx) => {
    if (val.setting === 'HOMECARE' && val.hstTreatment !== 'TAXABLE') {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['hstTreatment'],
        message: 'Homecare must be Taxable — only LTC rate cards may be exempt.',
      });
    }
  });

const SUPERSEDE_SCHEMA = z.object({
  newDefaultPayRate: z.string().regex(/^\d+(\.\d{1,4})?$/),
  newDefaultBillRate: z.string().regex(/^\d+(\.\d{1,4})?$/),
  hstTreatment: z.enum(['TAXABLE', 'EXEMPT', 'ZERO_RATED']),
  effectiveFrom: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  reason: z.string().min(10).max(500),
  overrideLeadTime: z.boolean().optional(),
  overrideReason: z.string().optional(),
});

const DEPRECATE_SCHEMA = z.object({
  effectiveTo: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  reason: z.string().min(10).max(500),
  overrideLeadTime: z.boolean().optional(),
  overrideReason: z.string().optional(),
});

// Edit (unused rows) — value fields only; no dates/reason/override. `setting`/
// `roleId` are frozen (backend rejects a homecare non-taxable edit inline).
const EDIT_SCHEMA = z.object({
  defaultPayRate: z.string().regex(/^\d+(\.\d{1,4})?$/, 'Enter a positive decimal'),
  defaultBillRate: z.string().regex(/^\d+(\.\d{1,4})?$/, 'Enter a positive decimal'),
  hstTreatment: z.enum(['TAXABLE', 'EXEMPT', 'ZERO_RATED']),
});

interface RateCardRow {
  id: string;
  roleId: string;
  roleCode: string;
  setting: string;
  defaultPayRate: string;
  defaultBillRate: string;
  hstTreatment: string;
  effectiveFrom: string;
  effectiveTo: string | null;
  isCurrent: boolean;
  inUse: boolean;
}

interface RoleOption {
  id: string;
  code: string;
  name: string;
}

function RateCardsPage() {
  // Role dropdown source — Phase 14e-a-9 lock added /admin/roles for
  // exactly this. Cached at the parent panel via React Query.
  const rolesQuery = useQuery({
    queryKey: ['admin', 'roles', { activeOnly: true }],
    queryFn: async () => {
       
      const r = (await unwrap(api.GET('/api/v1/admin/roles' as never))) as {
        items: RoleOption[];
      };
      return r;
    },
  });
  const roleOptions = (rolesQuery.data?.items ?? []).map((r) => ({
    value: r.id,
    label: `${r.code} — ${r.name}`,
  }));

  return (
    <SupersedableResourcePanel<RateCardRow>
      labels={{ panelTitle: 'Rate cards', singular: 'rate card', plural: 'rate cards' }}
      columns={[
        { key: 'roleCode', header: 'Role', width: 'w-24' },
        { key: 'setting', header: 'Setting', width: 'w-28' },
        {
          key: 'defaultPayRate',
          header: 'Pay ($/hr)',
          width: 'w-28',
          render: (r) => `$${r.defaultPayRate}`,
        },
        {
          key: 'defaultBillRate',
          header: 'Bill ($/hr)',
          width: 'w-28',
          render: (r) => `$${r.defaultBillRate}`,
        },
        { key: 'hstTreatment', header: 'HST', width: 'w-32' },
        { key: 'effectiveFrom', header: 'From', width: 'w-28' },
        {
          key: 'effectiveTo',
          header: 'To',
          width: 'w-28',
          render: (r) => r.effectiveTo ?? '—',
        },
      ]}
      endpoints={{
        list: async () => {
           
          const r = (await unwrap(
            api.GET('/api/v1/admin/rates/cards' as never, {
              params: { query: { includeHistorical: 'true' } },
            } as never),
          )) as { items: RateCardRow[] };
          return r;
        },
         
        create: (body) => unwrap(api.POST('/api/v1/admin/rates/cards' as never, { body } as never)),
        supersede: (id, body) =>
           
          unwrap(
            api.POST('/api/v1/admin/rates/cards/{id}/supersede' as never, {
              params: { path: { id } },
              body,
            } as never),
          ),
        deprecate: (id, body) =>

          unwrap(
            api.POST('/api/v1/admin/rates/cards/{id}/deprecate' as never, {
              params: { path: { id } },
              body,
            } as never),
          ),
        // Unused rows only (panel gates on inUse): in-place edit + delete.
        edit: (id, body) =>
          unwrap(
            api.PATCH('/api/v1/admin/rates/cards/{id}' as never, {
              params: { path: { id } },
              body,
            } as never),
          ),
        delete: (id) =>
          unwrap(
            api.DELETE('/api/v1/admin/rates/cards/{id}' as never, {
              params: { path: { id } },
            } as never),
          ),
      }}
      form={{
        createSchema: CREATE_SCHEMA,
        createFields: [
          { kind: 'select', name: 'roleId', label: 'Role', options: roleOptions },
          { kind: 'select', name: 'setting', label: 'Setting', options: SETTING_OPTIONS },
          { kind: 'money', name: 'defaultPayRate', label: 'Default pay rate' },
          { kind: 'money', name: 'defaultBillRate', label: 'Default bill rate' },
          { kind: 'select', name: 'hstTreatment', label: 'HST treatment', options: HST_OPTIONS },
        ],
        supersedeSchema: SUPERSEDE_SCHEMA,
        supersedeFields: [
          { kind: 'money', name: 'newDefaultPayRate', label: 'New pay rate' },
          { kind: 'money', name: 'newDefaultBillRate', label: 'New bill rate' },
          { kind: 'select', name: 'hstTreatment', label: 'HST treatment', options: HST_OPTIONS },
        ],
        supersedeDefaultValues: (entry) => ({
          newDefaultPayRate: entry.defaultPayRate,
          newDefaultBillRate: entry.defaultBillRate,
          hstTreatment: entry.hstTreatment,
        }),
        deprecateSchema: DEPRECATE_SCHEMA,
        editSchema: EDIT_SCHEMA,
        editFields: [
          { kind: 'money', name: 'defaultPayRate', label: 'Default pay rate' },
          { kind: 'money', name: 'defaultBillRate', label: 'Default bill rate' },
          { kind: 'select', name: 'hstTreatment', label: 'HST treatment', options: HST_OPTIONS },
        ],
        editDefaultValues: (entry) => ({
          defaultPayRate: entry.defaultPayRate,
          defaultBillRate: entry.defaultBillRate,
          hstTreatment: entry.hstTreatment,
        }),
      }}
      query={{ queryKey: ['admin', 'rates', 'cards'] }}
    />
  );
}
